import styled from "styled-components";
import { useState } from "react";
import { useDispatch } from 'react-redux'
import { setCredentials } from '../features/auth/authSlice'
import { useLoginSupportMutation } from '../features/auth/authApiSlice'
import usePersist from '../features/hooks/usePersist'
import { useNavigate, Link } from 'react-router-dom'
import useTitle from '../features/hooks/useTitle';
import { TextField, Paper, IconButton, InputAdornment, Checkbox, FormControlLabel } from "@mui/material";
import { CommonButton } from '../Styles/CommonStyles';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import cwcLogo from '../images/logo-final.png';

const SupportLoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #a961f1 0%, #57ccf4 100%);
  padding: 20px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="water" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse"><circle cx="10" cy="10" r="1" fill="%23ffffff" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23water)"/></svg>');
    pointer-events: none;
  }
`;

const SupportLoginWrapper = styled(Paper)`
  width: 100%;
  max-width: 450px;
  padding: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  border-radius: 20px;
  background: white;
  position: relative;
  z-index: 1;
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, #a961f1, #57ccf4);
  }
`;

const LogoContainer = styled.div`
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  width: 100%;
  position: relative;
`;

const LogoImage = styled.img`
  height: 95px;
  width: auto;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  flex-shrink: 0;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
`;

const SupportBadge = styled.div`
  position: absolute;
  top: -15px;
  right: -5px;
  background: linear-gradient(45deg, #a961f1, #57ccf4);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  transform: rotate(15deg);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 10;
`;

const LogoText = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #5C5891;
  margin: 0;
  line-height: 1.2;
`;

const Subtitle = styled.p`
  color: #a961f1;
  margin: 4px 0 0 0;
  font-size: 16px;
  font-weight: 500;
`;

const StyledForm = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const StyledTextField = styled(TextField)`
  & .MuiOutlinedInput-root {
    border-radius: 12px;
    background-color: #f8f9fa;
    
    &:hover .MuiOutlinedInput-notchedOutline {
      border-color: #a961f1;
    }
    
    &.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: #5C5891;
      border-width: 2px;
    }
  }
  
  & .MuiInputLabel-root.Mui-focused {
    color: #5C5891;
  }
`;

const SupportLoginButton = styled(CommonButton)`
  && {
    height: 50px;
    background: linear-gradient(45deg, #a961f1, #57ccf4);
    color: white;
    font-weight: 600;
    text-transform: none;
    border-radius: 12px;
    margin-top: 10px;
    font-size: 16px;
    box-shadow: 0 4px 15px rgba(169, 97, 241, 0.3);
    position: relative;
    overflow: hidden;
    
    &:hover {
      background: linear-gradient(45deg, #5C5891, #57ccf4);
      box-shadow: 0 6px 20px rgba(169, 97, 241, 0.4);
      transform: translateY(-1px);
    }
    
    &:disabled {
      background: #e0e0e0;
      box-shadow: none;
      transform: none;
    }

    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 4px;
      height: 100%;
      background: rgba(255, 255, 255, 0.3);
    }
  }
`;

const LinksContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 15px;
`;

const StyledLink = styled(Link)`
  color: #a961f1;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
    color: #5C5891;
  }
`;

const ErrorMessage = styled.div`
  color: #d32f2f;
  background-color: #ffebee;
  padding: 10px;
  border-radius: 12px;
  font-size: 14px;
  margin-top: 10px;
  text-align: center;
`;

const RememberMeContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 10px;
`;

const SupportLogin = () => {
  useTitle("Central Water Commission - Support Login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [persist, setPersist] = usePersist();
  const [login, { isLoading }] = useLoginSupportMutation();

  const handleToggle = () => setPersist(prev => !prev);

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleClick = async (e) => {
    e.preventDefault();
    setError("");

    if (!email?.length || !password?.length) {
      setError("Email and Password are required");
      return;
    }

    try {
      const response = await login({ email, password }).unwrap();
      const { accessToken } = response;
      if (accessToken?.length) {
        dispatch(setCredentials({ accessToken }));
        navigate('/home');
        setEmail('');
        setPassword('');
      } else {
        setError("Login Failed");
      }
    } catch (err) {
      setError(err?.data?.message || "Login failed. Please check your credentials.");
    }
  };

  return (
    <SupportLoginContainer>
      <SupportLoginWrapper elevation={3}>
        <LogoContainer>
          <SupportBadge>Support</SupportBadge>
          <LogoImage src={cwcLogo} alt="Central Water Commission Logo" />
          <TextContainer>
            <LogoText>Central Water Commission</LogoText>
            <Subtitle>Asset Management System</Subtitle>
          </TextContainer>
        </LogoContainer>

        <StyledForm onSubmit={handleClick}>
          <StyledTextField
            label="Email Address"
            onChange={(e) => setEmail(e.target.value)}
            value={email}
            variant="outlined"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlinedIcon sx={{ color: '#a961f1' }} />
                </InputAdornment>
              ),
            }}
          />

          <StyledTextField
            label="Password"
            onChange={(e) => setPassword(e.target.value)}
            type={showPassword ? "text" : "password"}
            value={password}
            variant="outlined"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon sx={{ color: '#a961f1' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleTogglePasswordVisibility} edge="end">
                    {showPassword ? <Visibility sx={{ color: '#a961f1' }} /> : <VisibilityOff sx={{ color: '#a961f1' }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <RememberMeContainer>
            <FormControlLabel
              control={
                <Checkbox
                  checked={persist}
                  onChange={handleToggle}
                  sx={{
                    color: '#a961f1',
                    '&.Mui-checked': {
                      color: '#5C5891',
                    },
                  }}
                />
              }
              label="Remember me"
            />
          </RememberMeContainer>

          <SupportLoginButton type="submit" disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} color="inherit" /> : "Sign In"}
          </SupportLoginButton>

          {error && <ErrorMessage>{error}</ErrorMessage>}
        </StyledForm>
      </SupportLoginWrapper>
    </SupportLoginContainer>
  );
};

export default SupportLogin;